
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { findQrSession, markQrSessionUsed } from '../../db/qrSessionRepository';
import QrLinkStatusCard from '../../components/qr/QrLinkStatusCard';
import QrLinkGuestPrompt from '../../components/qr/QrLinkGuestPrompt';

export default function QRLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'no-token' | 'expired' | 'used' | 'not-found' | 'success' | 'guest'>('loading');
  const [error, setError] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    handleQRLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQRLink = async () => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('no-token');
      return;
    }

    // 1. QR 세션 유효성 확인
    try {
      const session = await findQrSession(token);

      if (!session) {
        setStatus('not-found');
        return;
      }

      if (session.is_used) {
        setStatus('used');
        return;
      }

      const expiresAt = new Date(session.expires_at);
      if (expiresAt < new Date()) {
        setStatus('expired');
        return;
      }

      // 2. QR 세션에 auth 토큰이 있으면 자동 로그인 (로그인된 PC에서 생성한 QR)
      if (session.auth_access_token && session.auth_refresh_token) {
        const { data: authData, error: authError } = await supabase.auth.setSession({
          access_token: session.auth_access_token,
          refresh_token: session.auth_refresh_token,
        });

        if (authError) {
          // setSession 실패 이유를 error state에 기록 (디버깅용)
          setError(`[auto-login] ${authError.message}`);
        }

        if (!authError && authData.user) {
          await markQrSessionUsed(token, authData.user.id);
          localStorage.setItem('qr_auto_play', 'true');
          localStorage.setItem('qr_session_token', token);
          localStorage.setItem('qr_user_id', authData.user.id);
          setStatus('success');
          setTimeout(() => {
            navigate('/qr-select');
          }, 1500);
          return;
        }
      }

      // 3. 로그인 여부 확인
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // 이미 로그인된 사용자 → 세션 연결 처리
        await markQrSessionUsed(token, user.id);

        // 자동 재생 플래그 설정
        localStorage.setItem('qr_auto_play', 'true');
        localStorage.setItem('qr_session_token', token);
        localStorage.setItem('qr_user_id', user.id);

        setStatus('success');
        setTimeout(() => {
          navigate('/qr-select');
        }, 1500);
      } else {
        // 비로그인 사용자 → 회원가입 안내
        // 토큰을 저장해두어 회원가입/로그인 후 자동 연결할 수 있도록
        localStorage.setItem('pending_qr_token', token);
        setStatus('guest');
      }
    } catch {
      setStatus('not-found');
    }
  };

  // 로딩 중
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('qr.checking')}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <QrLinkStatusCard
        variant="success"
        iconClass="ri-check-line"
        title={t('qr.login_success')}
        description={t('qr.redirecting_remote')}
      />
    );
  }

  if (status === 'guest') {
    return (
      <QrLinkGuestPrompt
        welcomeTitle={t('qr.welcome')}
        welcomeSubtitle={t('qr.welcome_subtitle')}
        loginRequiredTitle={t('qr.login_required')}
        loginRequiredDesc={t('qr.login_required_desc')}
        features={[
          {
            iconClass: 'ri-smartphone-line',
            bgClass: 'bg-violet-100',
            iconColor: 'text-violet-600',
            title: t('qr.feature_remote'),
            description: t('qr.feature_remote_desc'),
          },
          {
            iconClass: 'ri-focus-3-line',
            bgClass: 'bg-teal-100',
            iconColor: 'text-teal-600',
            title: t('qr.feature_laser'),
            description: t('qr.feature_laser_desc'),
          },
          {
            iconClass: 'ri-timer-line',
            bgClass: 'bg-amber-100',
            iconColor: 'text-amber-600',
            title: t('qr.feature_timer'),
            description: t('qr.feature_timer_desc'),
          },
        ]}
        errorDetail={error || undefined}
        signupSlot={
          <Link
            to="/signup"
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white py-4 rounded-xl font-medium hover:from-violet-600 hover:to-purple-600 transition-all whitespace-nowrap"
          >
            <i className="ri-user-add-line text-xl"></i>
            {t('qr.signup_action')}
          </Link>
        }
        loginSlot={
          <Link
            to="/login"
            className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-all whitespace-nowrap"
          >
            <i className="ri-login-box-line"></i>
            {t('qr.has_account_login')}
          </Link>
        }
      />
    );
  }

  const errorConfig: Record<string, { icon: string; title: string; desc: string }> = {
    'no-token': { icon: 'ri-error-warning-line', title: t('qr.error_invalid'), desc: t('qr.error_invalid_desc') },
    'expired': { icon: 'ri-time-line', title: t('qr.error_expired'), desc: t('qr.error_expired_desc') },
    'used': { icon: 'ri-checkbox-circle-line', title: t('qr.error_used'), desc: t('qr.error_used_desc') },
    'not-found': { icon: 'ri-search-line', title: t('qr.error_not_found'), desc: t('qr.error_not_found_desc') },
  };
  const config = errorConfig[status] || errorConfig['not-found'];

  return (
    <QrLinkStatusCard
      variant="error"
      iconClass={config.icon}
      title={config.title}
      description={config.desc}
      errorDetail={error || undefined}
      homeLabel={t('qr.go_home')}
      onGoHome={() => navigate('/')}
    />
  );
}
